<template>
  <div class="rtc-live-classroom">
    <!-- 顶部导航栏 -->
    <header class="classroom-header">
      <div class="header-left">
        <div class="course-info">
          <h2 class="course-title">{{ roomInfo.roomName }}</h2>
        </div>
      </div>
      <div class="header-right">
        <!-- AI 课堂状态：原右侧面板内容上移到头部，仅 AI 上课模式展示 -->
        <template v-if="aiPptMode">
          <span class="header-class-progress">
            第 {{ aiPageNumber }} / {{ aiTotalPage || '-' }} 页
            <span class="ai-qa-flag" v-if="classEnded">已下课</span>
          </span>
          <span class="header-class-divider"></span>
        </template>
        <!-- RTC 通道状态：音视频走 DingRTC，与下方的课堂 WebSocket 通道并存 -->
        <el-tag :type="connectionStatus === 'connected' ? 'success' : 'warning'" size="small">RTC {{ connectionStatusText }}</el-tag>
        <!-- 课堂 WebSocket 通道状态：仅 AI 上课模式存在，负责接收逐页课件与指令 -->
        <el-tag v-if="aiPptMode" :type="classWsStatusType" size="small">课堂 {{ classWsStatusText }}</el-tag>
      </div>
    </header>

    <!-- 主体内容区 -->
    <div class="classroom-body">
      <!-- 左侧：视频区域 -->
      <div class="video-section">
        <!-- 老师屏幕共享大屏 -->
        <div class="teacher-video-container">
          <!-- 屏幕共享数据流 -->
          <div id="screen-share-video" class="screen-share-video"></div>
          <!-- AI 上课模式：aiFlag=1 且课件地址非空时展示 PPT 课件；
               PPT 由前端解析 pptx 直接渲染，翻页由后端下发的 pageNumber 驱动，学生端不提供手动翻页 -->
          <div class="ppt-courseware" v-if="aiPptMode && !screenSharing">
            <PptxPreview
              :src="roomInfo.aiPPTUrl"
              height="100%"
              :show-controls="false"
              :page-number="aiPageNumber"
            ></PptxPreview>
            <div class="ppt-courseware-label">
              <i class="el-icon-reading"></i> AI 课件演示中
              <template v-if="aiTotalPage"> · 第 {{ aiPageNumber }} / {{ aiTotalPage }} 页</template>
            </div>
            <!-- 浏览器自动播放策略会拦截无交互的音频，这里给出显式解锁入口 -->
            <div class="ai-audio-unlock" v-if="aiAudioBlocked" @click="unlockAiAudio">
              <i class="el-icon-video-play"></i> 点击开启声音
            </div>
          </div>
          <div class="video-placeholder" v-if="!screenSharing && !aiPptMode">
            <i class="el-icon-monitor"></i>
            <p>等待老师共享屏幕...</p>
          </div>
          <div class="screen-share-label" v-if="screenSharing">
            <i class="el-icon-monitor"></i> {{ roomInfo.teacherName }} 正在共享屏幕
          </div>
        </div>
      </div>
    </div>
    <!-- 底部导航栏（风格与顶部导航栏一致，后续功能扩展预留） -->
    <footer class="classroom-footer">
      <!-- 底部控制栏：我的摄像头预览 + 麦克风/摄像头开关合并为一体；老师摄像头同样下沉到底部（仅展示，不支持开关控制） -->
      <div class="control-bar">
        <!-- 我的摄像头 + 麦克风/摄像头开关（开关小图标叠加在预览窗内右下角） -->
        <div class="camera-bar self-camera-bar">
          <div class="camera-bar-video-wrap">
            <div id="student-camera-video" class="camera-bar-video"></div>
            <div class="camera-bar-placeholder" v-if="!cameraOn">
              <i class="el-icon-video-camera"></i>
              <span>未开启</span>
            </div>
            <div class="camera-bar-actions">
              <el-tooltip :content="!micOn ? '打开麦克风' : '关闭麦克风'" placement="top">
                <div class="camera-action-btn" :class="{ 'muted': !micOn }" @click="toggleMic">
                  <i :class="!micOn ? 'el-icon-turn-off-microphone' : 'el-icon-microphone'"></i>
                </div>
              </el-tooltip>
              <el-tooltip :content="!cameraOn ? '打开摄像头' : '关闭摄像头'" placement="top">
                <div class="camera-action-btn" :class="{ 'muted': !cameraOn }" @click="toggleCamera">
                  <i :class="!cameraOn ? 'el-icon-video-camera' : 'el-icon-video-camera-solid'"></i>
                </div>
              </el-tooltip>
            </div>
          </div>
        </div>
        <!-- 老师摄像头（仅展示，不支持开关控制，尺寸与原开关按钮一致） -->
        <div class="camera-bar teacher-camera-bar">
          <div class="camera-bar-video-wrap">
            <div id="teacher-camera-video" class="camera-bar-video"></div>
            <div class="camera-bar-placeholder" v-if="!teacherCameraReady">
              <i class="el-icon-video-camera"></i>
            </div>
          </div>
          <span class="teacher-bar-name">{{ roomInfo.teacherName }}</span>
        </div>
        <!-- AI 上课字幕：固定在控制栏正中，一次只显示当前正在朗读的那一句。
             以 .control-bar 为绝对定位基准水平居中（两侧摄像头/按钮宽度不等，
             靠 flex 占位会被挤偏），不拦截点击。 -->
        <div class="ai-subtitle" v-if="aiPptMode && subtitleVisible">
          {{ subtitleText }}
        </div>
        <div class="control-right">
          <!-- AI 上课操作按钮：从原右侧面板迁移到底部右侧 -->
          <template v-if="aiPptMode">
            <!-- 开始上课：建会话 + 连 WebSocket，由后端开始逐页下发讲稿与语音 -->
            <el-button
              size="mini"
              type="primary"
              :loading="classStarting"
              :disabled="!classCanStart"
              @click="startAiClass"
            >开始上课</el-button>
            <!-- 从头开始：页码退回第 1 页重新讲，课堂还是同一节 -->
            <el-button
              size="mini"
              type="warning"
              plain
              :disabled="!classWsConnected"
              @click="restartAiClass"
            >从头开始</el-button>
            <!-- 结束本节课：告知后端下课并断开通道 -->
            <el-button
              size="mini"
              type="danger"
              plain
              :disabled="!classWsConnected"
              @click="stopAiClass"
            >结束本节课</el-button>
            <span class="control-right-divider"></span>
          </template>
          <el-tooltip content="设置" placement="top">
            <div class="control-btn" @click="showSettings = true">
              <i class="el-icon-setting"></i>
            </div>
          </el-tooltip>
          <el-tooltip content="全屏" placement="top">
            <div class="control-btn" @click="toggleFullscreen">
              <i class="el-icon-full-screen"></i>
            </div>
          </el-tooltip>
        </div>
      </div>
    </footer>
    <!-- 设置弹窗 -->
    <el-dialog title="设置" :visible.sync="showSettings" width="420px" :modal-append-to-body="false">
      <div class="settings-content">
        <div class="setting-item">
          <span class="setting-label">麦克风</span>
          <el-select v-model="selectedMic" placeholder="选择麦克风" size="small">
            <el-option
              v-for="device in micDevices"
              :key="device.deviceId"
              :label="device.label || '麦克风 ' + device.deviceId.slice(0, 8)"
              :value="device.deviceId"
            ></el-option>
          </el-select>
        </div>
        <div class="setting-item">
          <span class="setting-label">摄像头</span>
          <el-select v-model="selectedCamera" placeholder="选择摄像头" size="small">
            <el-option
              v-for="device in cameraDevices"
              :key="device.deviceId"
              :label="device.label || '摄像头 ' + device.deviceId.slice(0, 8)"
              :value="device.deviceId"
            ></el-option>
          </el-select>
        </div>
        <div class="setting-item">
          <span class="setting-label">扬声器</span>
          <el-select v-model="selectedSpeaker" placeholder="选择扬声器" size="small">
            <el-option
              v-for="device in speakerDevices"
              :key="device.deviceId"
              :label="device.label || '扬声器 ' + device.deviceId.slice(0, 8)"
              :value="device.deviceId"
            ></el-option>
          </el-select>
        </div>
        <div class="setting-item">
          <span class="setting-label">视频质量</span>
          <el-select v-model="videoQuality" size="small">
            <el-option label="流畅 (360P)" value="360p"></el-option>
            <el-option label="标清 (480P)" value="480p"></el-option>
            <el-option label="高清 (720P)" value="720p"></el-option>
            <el-option label="超清 (1080P)" value="1080p"></el-option>
          </el-select>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="showSettings = false">取消</el-button>
        <el-button type="primary" @click="saveSettings">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from "@/common/api";
import DingRTC from 'dingrtc'
import PptxPreview from '@/components/PptxPreview.vue';

/**
 * AI 上课语音的 PCM 参数：后端 TTS 固定输出 16000Hz / 16bit / 单声道。
 * 播放时必须按这个采样率建 AudioBuffer，不能拿 AudioContext.sampleRate 顶替：
 * 设备默认采样率通常是 48000，用它去装 16000 的数据会把语速拉快三倍、音调变尖。
 */
const PCM_SAMPLE_RATE = 16000;
const PCM_CHANNELS = 1;

/**
 * 课后端下发的消息类型（与 ClassSessionWork 中的常量一一对应）
 * 文本帧 = 控制指令，二进制帧 = PCM 音频分片
 */
const MSG_SCRIPT_AUDIO = 'scriptAudio';        // 推一页讲稿 + 该页音频
const MSG_SCRIPT_AUDIO_DONE = 'scriptAudioDone'; // 本页音频已全部下发
const MSG_CLASS_END = 'classEnd';              // 课堂结束
const MSG_HEARTBEAT = 'heartbeat';             // 心跳保活（生成讲稿期间后端定期发，防 nginx 掐连接）

/** 前端上报的消息类型 */
const CMD_SCRIPT_AUDIO_FINISHED = 'scriptAudioFinished'; // 本页播完了，后端据此翻页
const CMD_STOP = 'stop';                                 // 主动下课
const CMD_RESTART = 'restart';                           // 从头开始学习

export default {
  name: 'LiveVideo',
  components: { PptxPreview },
  data() {
    return {
      // ===== 教室信息 =====
      roomInfo: {
        roomId: 1,
        roomName: '',
        appId: '',
        token: '',
        uid: '',
        channel: '',
        userName: '',
        teacherName: '',
        liveTeacherId: '',
        // AI 上课相关（enterLive 返回）：aiFlag=1 表示 AI 上课，此时展示 aiPPTUrl 指向的 PPT 课件，
        // 并用 courseWareId 调开课接口，由后端按页下发讲稿与音频
        aiFlag: '0',
        aiPPTUrl: '',
        courseWareId: null,
        // 上课的学生ID（enterLive 返回）：家长账号可能绑多个孩子，开课时必须指定是哪个孩子，
        // 后端据此校验他确实在本直播间，并把课堂会话、进度绑定到他身上
        studentId: null
      },
      // RTC 相关
      client: null,
      micTrack: null,
      cameraTrack: null,
      micOn: false,
      cameraOn: false,

      connectionStatus: 'disconnected', // disconnected | connecting | connected | reconnecting
      connectionStatusText: '未连接',
      mcuSubscribed: false,

      //老师屏幕共享 / 摄像头悬浮窗状态
      screenSharing: false,          // 老师是否正在共享屏幕
      teacherScreenTrack: null,      // 老师屏幕共享轨道
      teacherCameraReady: false,     // 老师摄像头视频是否就绪
      teacherVideoTrack: null,       // 老师摄像头轨道
      teacherMicReady: false,     // 老师摄像头视频是否就绪
      teacherAudioTrack: null,       // 老师摄像头轨道

      // 设置
      showSettings: false,
      micDevices: [],
      cameraDevices: [],
      speakerDevices: [],
      selectedMic: '',
      selectedCamera: '',
      selectedSpeaker: '',
      currentMic: '',      // 当前实际使用的麦克风（用于设置变更检测）
      currentCamera: '',   // 当前实际使用的摄像头（用于设置变更检测）
      videoQuality: '720p',
      //防止重复订阅
      subscribedSet: new Set(),
      //操作锁：防止麦克风/摄像头快速重复点击引发并发竞态
      micToggling: false,
      cameraToggling: false,
      //保存 client 事件监听句柄，便于销毁/失败时解绑
      clientHandlers: {},

      // ===== AI 上课 · 课堂 WebSocket（与 RTC 并存，负责接收逐页讲稿与语音） =====
      classWs: null,                 // 课堂 WebSocket 实例
      classWsStatus: 'idle',         // idle | connecting | connected | disconnected | error
      classSessionId: '',            // 后端开课返回的会话ID，WebSocket 握手时作为 sessionId 携带
      classStarting: false,          // 开课接口请求中（按钮 loading）
      classEnded: false,             // 是否已收到下课通知
      aiTotalPage: 0,                // 课件完整页数（enterLive 的 courseWarePageSize 下发；非可讲页数）
      aiPageNumber: 1,               // 当前页的真实幻灯片页码（1开始），由后端下发驱动，前端不翻页
      aiScript: '',                  // 当前页讲稿文本（后端下发，展示用）
      aiAudioBlocked: false,         // 音频被浏览器自动播放策略拦截，需用户点击解锁

      // ===== AI 上课 · 字幕跟读（按语音播报逐句显示） =====
      // 后端已把单字时间戳按标点聚合成「句」，直接放在 words 里下发：
      // 每项是 { text: 整句文本, beginTime: 句首毫秒, endTime: 句末毫秒 }。
      // 字段名虽然叫 words，实际粒度是句，前端按句切换即可，不需要自己切。
      aiSentences: [],
      pageAudioStarted: false,       // 本页音频是否已起播（起播的那一帧才记起点，避免重复记）
      pageAudioStartTime: 0,         // 本页音频在 AudioContext 时间轴上的起点（秒）
      subtitleStopMs: 0,             // 本页字幕最多显示到多少毫秒（=音频总时长），超额自动清屏
      subtitleElapsedMs: 0,          // 当前播放进度（毫秒），字幕渲染完全由它驱动
      subtitleTimer: null,           // 播放进度采样定时器（句切换靠它推）

      // ===== AI 上课 · PCM 播放队列（后端下发裸 PCM 二进制帧，按序衔接播放） =====
      playbackAudioContext: null,    // 播放用 AudioContext（首次收到音频时才创建）
      playbackGainNode: null,        // 播放增益节点
      pcmBufferQueue: [],            // 待排程的PCM数据（ArrayBuffer），收到即入队
      nextPlayTime: 0,               // 下一段的排程起点，保证段与段首尾无缝
      audioDone: false,              // 本页音频是否已全部下发（收到 scriptAudioDone）
      pageFinishTimer: null,         // 播完检测定时器
    }
  },
  computed: {
    // AI 上课模式：aiFlag=1（后端为字符串"1"/"0"）且课件地址非空，等待区域改用 PPT 课件展示
    aiPptMode() {
      return String(this.roomInfo.aiFlag) === '1' && !!this.roomInfo.aiPPTUrl;
    },
    // 课堂 WebSocket 是否已连通（不能重复开课、能不能下课/重讲都看它）
    classWsConnected() {
      return this.classWsStatus === 'connected';
    },
    // 是否可以点「开始上课」：必须是 AI 模式、通道空闲、且还没开始
    classCanStart() {
      return this.aiPptMode && !this.classStarting && !this.classWsConnected && !this.classSessionId;
    },
    classWsStatusType() {
      const map = {
        idle: 'info',
        connecting: 'warning',
        connected: 'success',
        disconnected: 'info',
        error: 'danger'
      };
      return map[this.classWsStatus] || 'info';
    },
    classWsStatusText() {
      const map = {
        idle: '未开始',
        connecting: '连接中',
        connected: '上课中',
        disconnected: '已断开',
        error: '连接失败'
      };
      return map[this.classWsStatus] || '未知';
    },
    /**
     * 字幕是否显示：本页有内容、课程进行中，且进度还没跑出本页音频范围
     * 进度超出音频总时长（含尾部静音/停顿）即视为本页念完，字幕淡出
     * <p>
     * 判「有内容」用 script 或句级时间轴任一非空：后端是 SSML 送合成，
     * 若拿纯文本化的 script 恰好为空（标签被清洗掉），句级数据仍在，
     * 只认 script 会让字幕白屏。
     */
    subtitleVisible() {
      if (this.classEnded) {
        return false;
      }
      if (!this.aiScript && (!this.aiSentences || this.aiSentences.length === 0)) {
        return false;
      }
      if (this.subtitleStopMs > 0 && this.subtitleElapsedMs > this.subtitleStopMs) {
        return false;
      }
      return true;
    },
    /**
     * 当前正在朗读的那一句文本（后端下发的句级时间轴里，进度落在哪句就显示哪句）
     * <p>
     * 后端已把单字时间戳按标点聚合成「句」，随 words 字段下发，每项形如
     * { text: 整句文本, beginTime: 句首毫秒, endTime: 句末毫秒 }。
     * 前端只做「按时间挑句」这一件事，不做任何切分，也不做句内逐字点亮
     * （后端这一路没下发字级索引，硬做只会串字）：
     * 判定用「本句起始时间 <= 当前进度」，取满足条件的最后一条——
     * 句子一开口就整句亮出，句末读完、下一句还没开口时仍停在上一句，不会闪空白。
     */
    subtitleText() {
      const sentences = this.aiSentences;
      if (!sentences || sentences.length === 0) {
        // 降级：后端没下发句级时间轴时，退回整段讲稿，至少不空屏
        return this.aiScript;
      }
      const ms = this.subtitleElapsedMs;
      let hit = null;
      for (let i = 0; i < sentences.length; i++) {
        if (sentences[i].beginTime <= ms) {
          hit = sentences[i];
        } else {
          break;
        }
      }
      // 还没开口：先亮出第一句，避免字幕空白
      return (hit || sentences[0]).text || '';
    }
  },
  created() {
    this.initRoomInfo();
  },
  beforeDestroy() {
    // 页面销毁时释放 RTC 资源
    try {
      // 1. 解绑 client 事件监听，避免残留回调与内存泄漏
      if (this.client && this.clientHandlers) {
        Object.keys(this.clientHandlers).forEach((event) => {
          try {
            this.client.off && this.client.off(event, this.clientHandlers[event]);
          } catch (e) { console.error(`解绑事件 ${event} 失败:`, e); }
        });
        this.clientHandlers = {};
      }
      // 2. 停止并关闭所有本地/远端轨道（不依赖 client 是否存在）
      const tracks = [this.micTrack, this.cameraTrack, this.teacherScreenTrack, this.teacherVideoTrack, this.teacherAudioTrack];
      tracks.forEach((track) => {
        if (track) {
          try {
            track.stopPlay && track.stopPlay();
            track.stop && track.stop();
            track.close && track.close();
          } catch (e) { console.error('清理轨道失败:', e); }
        }
      });
      this.micTrack = null;
      this.cameraTrack = null;
      this.teacherScreenTrack = null;
      this.teacherVideoTrack = null;
      this.teacherAudioTrack = null;
      // 3. 退出房间，释放服务端资源
      if (this.client) {
        this.client.leave();
      }
    } catch (error) {
      console.error('清理资源失败:', error)
    }
    // 释放 AI 上课占用的资源：课堂 WebSocket、当前页音频、PCM 播放上下文
    this.releaseAiClass();
  },
  methods: {
    async initRoomInfo(){
      const { id: planId, section } = this.$route.query || {};
      if (!planId || !section) {
        this.$message.error('缺少直播参数，请从课程页面进入直播教室');
        console.error('initRoomInfo error: 缺少 planId 或 section 参数');
        return;
      }
      try {
        const res=await api.enter_live({planId, section});
        if (res?.data?.code === "200" && res.data.data) {
          this.roomInfo = { ...this.roomInfo, ...res.data.data };
          await this.initClassroom();
        } else {
          this.$message.error('获取直播房间信息失败');
        }
      } catch(err) {
        this.$message.error('初始化直播房间失败');
        console.error('initRoomInfo error:', err);
      }
    },
    async subscribeUser(user, mediaType, auxiliary){
      const userId = user?.userId;
      if (!userId || String(userId) === String(this.roomInfo.uid)) {
        return;
      }
      //仅处理老师用户的流
      if (!String(userId).startsWith('TEACHER_')) {
        return;
      }
      const key=userId+"_"+mediaType+"_"+auxiliary;
      if(this.subscribedSet.has(key)){
        return;
      }
      this.subscribedSet.add(key);
      try {
        if (mediaType === 'video') {
          if (auxiliary) {
            //老师屏幕共享数据流 → 大屏展示
            const track = await this.client.subscribe(userId,mediaType,true);
            if (track) {
              this.teacherScreenTrack = track;
              track.play('#screen-share-video');
              this.screenSharing = true;
            }
          } else {
            const track = await this.client.subscribe(userId,mediaType,false);
            if (track) {
              this.teacherVideoTrack = track;
              this.teacherCameraReady = true;
              track.play('#teacher-camera-video');
            }
          }
        }
        if (mediaType === 'audio' && user.hasAudio) {
          if(!this.mcuSubscribed) {
            this.mcuSubscribed = true;
            try {
              const track = await this.client.subscribe('mcu', 'audio');
              if (track) { track.play(); }
            } catch(err) {
              this.mcuSubscribed = false;
              console.error(`subscribe mcu failed:`, err);
            }
          }
        }
      } catch(err) {
        // 订阅失败时移除标记，允许后续重试
        this.subscribedSet.delete(key);
        console.error(`subscribe ${key} failed:`, err);
      }
    },
    async unSubscribeUser(user, mediaType, auxiliary){
      const userId = user?.userId;
      if (!userId || String(userId) === String(this.roomInfo.uid)) {
        return;
      }
      if (!String(userId).startsWith('TEACHER_')) {
        return;
      }
      // 移除订阅标记，确保老师再次共享/开摄像头时可重新订阅
      const key=userId+"_"+mediaType+"_"+auxiliary;
      this.subscribedSet.delete(key);
      if (mediaType === 'video') {
        if (auxiliary) {
          //老师停止屏幕共享
          this.screenSharing = false;
          try {
            await this.client.unsubscribe(userId, mediaType, true);
          } catch (e) { console.error('unsubscribe screen failed:', e); }
        } else {
          //老师关闭摄像头
          this.teacherCameraReady = false;
          try {
            await this.client.unsubscribe(userId, mediaType, false);
          } catch (e) { console.error('unsubscribe camera failed:', e); }
        }
      }
    },
    async remoteUserJoined(user) {
      const userId = user?.userId;
      if (!userId || String(userId) === String(this.roomInfo.uid)) {
        return;
      }
      //仅处理老师用户的流
      if (!String(userId).startsWith('TEACHER_')) {
        return;
      }
      console.log('remoteUserJoined:', user);
    },
    async remoteUserLeaved(user){
      const userId = user?.userId;
      if (!userId || String(userId) === String(this.roomInfo.uid)) {
        return;
      }
      //仅处理老师用户的流
      if (!String(userId).startsWith('TEACHER_')) {
        return;
      }
      this.teacherScreenTrack=null;
      this.teacherVideoTrack=null;
    },
    // ==================== 初始化 ====================
    async initClassroom() {
      let supported = false;
      try {
        supported = DingRTC.checkSystemRequirements();
      } catch (e) {
        console.error('检测系统支持失败:', e);
      }
      if (!supported) {
        //不支持webrtc
        this.$message.error('不支持webrtc,请使用chrome高版本浏览器');
        return;
      }
      try {
        this.client = DingRTC.createClient();
        // 保存监听句柄，便于销毁/失败时解绑
        const handlers = {
          'user-published': (user, mediaType, auxiliary) => {
            this.subscribeUser(user,mediaType,auxiliary);
          },
          'user-unpublished': (user, mediaType, auxiliary) => {
            this.unSubscribeUser(user,mediaType,auxiliary);
          },
          'user-joined': (user) => {
            this.remoteUserJoined(user);
          },
          'user-left': (user) => {
            this.remoteUserLeaved(user);
          },
          'connection-state-change': (status) => {
            this.connectionStatus = status;
            const statusTextMap = {
              connected: '已连接',
              connecting: '连接中',
              reconnecting: '重连中',
              disconnected: '已断开'
            };
            this.connectionStatusText = statusTextMap[status] || status || '未知状态';
          }
        };
        this.clientHandlers = handlers;
        Object.keys(handlers).forEach((event) => {
          this.client.on(event, handlers[event]);
        });
        await this.client.join({
          appId:this.roomInfo.appId,
          token:this.roomInfo.token,
          uid:this.roomInfo.uid,
          channel:this.roomInfo.channel,
          userName: this.roomInfo.userName
        });
        this.connectionStatus = 'connected';
        this.connectionStatusText = '已连接';
        for (const user of this.client.remoteUsers) {
          if (user?.hasVideo) {
            await this.subscribeUser(user, 'video', false);
          }
          if (user?.hasAudio) {
            await this.subscribeUser(user, 'audio', false);
          }
        }
        this.$message.success('欢迎来到直播教室！')
        //获取设备列表
        await this.getDevices();
        // 总页数：唯一来源，enterLive 已下发完整课件页数（未过滤的幻灯片总数）。
        // 后端上课通道不再下发 totalPage，所以这里填一次即可，全程不会被覆盖。
        if (this.roomInfo.courseWarePageSize) {
          this.aiTotalPage = this.roomInfo.courseWarePageSize;
        }
        // 注意：AI 上课不在这里自动开课，由学生点面板上的「开始上课」按钮触发（见 startAiClass）
      } catch(error) {
        // 加入失败：解绑事件监听并清理 client，避免残留监听导致重复订阅
        if (this.client) {
          try {
            Object.keys(this.clientHandlers || {}).forEach((event) => {
              this.client.off && this.client.off(event, this.clientHandlers[event]);
            });
            this.client.leave && this.client.leave();
          } catch (e) { console.error('清理 client 失败:', e); }
          this.clientHandlers = {};
        }
        this.client = undefined;
        this.connectionStatus = 'disconnected'
        this.connectionStatusText = '连接失败'
        this.$message.error('加入教室失败: ' + (error.message || '未知错误'))
      }
    },
    async getDevices() {
      try {
        const devices = await DingRTC.getDevices()
        this.micDevices = devices.filter(d => d.kind === 'audioinput')
        this.cameraDevices = devices.filter(d => d.kind === 'videoinput')
        this.speakerDevices = devices.filter(d => d.kind === 'audiooutput')

        if (this.micDevices.length > 0) {
          this.selectedMic = this.micDevices[0].deviceId
          this.currentMic = this.selectedMic
        }
        if (this.cameraDevices.length > 0) {
          this.selectedCamera = this.cameraDevices[0].deviceId
          this.currentCamera = this.selectedCamera
        }
        if (this.speakerDevices.length > 0) {
          this.selectedSpeaker = this.speakerDevices[0].deviceId
        }
      } catch (error) {
        console.error('获取设备列表失败:', error)
      }
    },
    //麦克风操作
    async toggleMic() {
      if (!this.client) {
        this.micOn = false;
        this.$message.error('进入课堂失败');
        return;
      }
      if (this.micToggling) {
        // 防止并发点击导致状态错乱
        return;
      }
      this.micToggling = true;
      const targetState = !this.micOn;
      try {
        if (targetState) {
          //没有麦克风轨道就创建一个
          if(!this.micTrack){this.micTrack = await DingRTC.createMicrophoneAudioTrack();}
          //发布麦克风轨道
          await this.client.publish([this.micTrack]);
          this.micOn = true;
          this.$message.success('麦克风推流成功');
        } else {
          //取消发布麦克风轨道
          if (this.micTrack) {await this.client.unpublish([this.micTrack]);}
          this.micOn = false;
          this.$message.success('麦克风取消推流');
        }
      } catch(err) {
        // 操作失败，状态保持原样
        console.error('toggleMic error:', err);
        this.$message.error('麦克风操作失败');
      } finally {
        this.micToggling = false;
      }
    },
    async toggleCamera() {
      if (!this.client) {
        this.cameraOn = false;
        this.$message.error('进入课堂失败');
        return;
      }
      if (this.cameraToggling) {
        // 防止并发点击导致状态错乱
        return;
      }
      this.cameraToggling = true;
      const targetState = !this.cameraOn;
      try {
        if (targetState) {
          if(!this.cameraTrack){
            this.cameraTrack = await DingRTC.createCameraVideoTrack({frameRate: 15, dimension: this.getVideoDimension()});
          }
          await this.client.publish([this.cameraTrack]);
          await this.cameraTrack.play('#student-camera-video');
          this.cameraOn = true;
          this.$message.success('视频推流成功');
        } else {
          if (this.cameraTrack) {
            await this.client.unpublish([this.cameraTrack]);
            this.cameraTrack.stopPlay();
            this.cameraOn = false;
            this.$message.success('视频取消推流');
          }
        }
      } catch(err) {
        // 操作失败，状态保持原样
        console.error('toggleCamera error:', err);
        this.$message.error('摄像头操作失败');
      } finally {
        this.cameraToggling = false;
      }
    },

    toggleFullscreen() {
      const doc = document;
      const elem = doc.querySelector('.rtc-live-classroom')
      if (!elem) {
        console.error('未找到全屏容器');
        return;
      }
      const isFullscreen = doc.fullscreenElement || doc['webkitFullscreenElement'];
      if (!isFullscreen) {
        const requestFn = elem.requestFullscreen || elem['webkitRequestFullscreen'];
        if (requestFn) {
          const promise = requestFn.call(elem);
          // Safari 等环境可能返回 undefined
          if (promise && typeof promise.catch === 'function') {
            promise.catch(err => {
              console.error('全屏失败:', err)
            })
          }
        }
      } else {
        const exitFn = doc.exitFullscreen || doc['webkitExitFullscreen'];
        if (exitFn) { exitFn.call(doc); }
      }
    },

    // ==================== 设置 ====================
    async saveSettings() {
      this.showSettings = false
      // 应用新的设备设置（仅对已创建的轨道生效，需重新推流）
      try {
        // 麦克风设备切换：重建轨道
        if (this.micTrack && this.selectedMic && this.selectedMic !== this.currentMic) {
          const newTrack = await DingRTC.createMicrophoneAudioTrack({ deviceId: this.selectedMic });
          if (this.micOn) {
            await this.client.publish([newTrack]);
          }
          this.micTrack.close();
          this.micTrack = newTrack;
          this.currentMic = this.selectedMic;
        }
        // 摄像头设备/清晰度切换：重建轨道
        if (this.cameraTrack && this.selectedCamera && this.selectedCamera !== this.currentCamera) {
          const newTrack = await DingRTC.createCameraVideoTrack({ frameRate: 15, dimension: this.getVideoDimension(), deviceId: this.selectedCamera });
          if (this.cameraOn) {
            await this.client.publish([newTrack]);
            await newTrack.play('#student-camera-video');
          }
          this.cameraTrack.close();
          this.cameraTrack = newTrack;
          this.currentCamera = this.selectedCamera;
        } else if (this.cameraTrack && this.cameraOn) {
          // 仅清晰度变化：提示重新开启摄像头生效
          this.$message.info('清晰度设置将在下次开启摄像头时生效');
        }
        this.$message.success('设置已保存')
      } catch (err) {
        console.error('saveSettings error:', err);
        this.$message.error('设置保存失败');
      }
    },
    // ==================== AI 上课 · 课堂 WebSocket ====================
    /**
     * 开始上课（主动触发）
     * 先调后端开课接口拿 sessionId，再建立课堂 WebSocket；之后后端逐页下发讲稿与语音
     */
    async startAiClass() {
      if (this.classStarting || this.classSessionId || this.classWsConnected) {
        // 防止重复点击：同一页面只开一次课
        return;
      }
      const roomId = this.roomInfo.roomId;
      const courseWareId = this.roomInfo.courseWareId;
      // 学生ID由 enterLive 下发：家长账号可能绑多个孩子，开课必须指明是谁，
      // 后端会校验这个孩子确实在本直播间（不在则开课失败）
      const studentId = this.roomInfo.studentId;
      if (!roomId || !courseWareId || !studentId) {
        this.$message.error('缺少开课参数，请返回课程页面重新进入直播');
        console.warn('startAiClass 缺少 roomId / courseWareId / studentId:', roomId, courseWareId, studentId);
        return;
      }
      this.classStarting = true;
      try {
        const res = await api.begin_class({ roomId, studentId, courseWareId });
        if (res?.data?.code !== '200' || !res.data.data) {
          this.$message.error('开始上课失败：' + ((res && res.data && res.data.msg) || '未知错误'));
          return;
        }
        // 后端返回的就是 sessionId 字符串本身，不是对象
        this.classSessionId = String(res.data.data);
        this.connectClassWs(this.classSessionId);
      } catch (err) {
        console.error('startAiClass error:', err);
        this.$message.error('开始上课失败');
      } finally {
        this.classStarting = false;
      }
    },

    /**
     * 建立课堂 WebSocket
     * 地址与页面同源：开发环境由 vue 代理转发(需 ws:true)，生产环境由 nginx 转发
     * 后端 /audio 没有 context-path，/api 只是网关/代理前缀
     */
    connectClassWs(sessionId) {
      this.disconnectClassWs();
      const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
      const url = protocol + window.location.host + '/api/audio?sessionId=' + encodeURIComponent(sessionId);
      this.classWsStatus = 'connecting';
      try {
        const ws = new WebSocket(url);
        // 后端下发 PCM 二进制帧，声明为 arraybuffer 便于直接解码播放
        ws.binaryType = 'arraybuffer';
        ws.onopen = () => {
          this.classWsStatus = 'connected';
          // 新一轮开课：清掉上一节的「已下课」标记
          this.classEnded = false;
          console.log('课堂 WebSocket 已连接:', url);
        };
        ws.onmessage = (event) => this.handleClassWsMessage(event);
        ws.onerror = (err) => {
          this.classWsStatus = 'error';
          console.error('课堂 WebSocket 错误:', err);
        };
        ws.onclose = (event) => {
          // 主动断开时状态已被 disconnectClassWs 收尾，这里不覆盖
          if (this.classWsStatus !== 'idle') {
            this.classWsStatus = 'disconnected';
            // 非主动断开（后端下课、网络异常）：连接没了，会话也就用不了了，
            // 顺手清掉 sessionId，让「开始上课」能重新点，不会卡死在「已开课」状态
            this.classSessionId = '';
          }
          this.stopPcmPlayback();
          console.log('课堂 WebSocket 已关闭, code:', event.code, 'reason:', event.reason);
        };
        this.classWs = ws;
      } catch (err) {
        this.classWsStatus = 'error';
        console.error('创建课堂 WebSocket 失败:', err);
        this.$message.error('课堂连接失败');
      }
    },

    /**
     * 断开课堂 WebSocket（解绑回调后再关闭，避免关闭时触发无意义的状态变更）
     */
    disconnectClassWs() {
      if (this.classWs) {
        this.classWs.onopen = null;
        this.classWs.onmessage = null;
        this.classWs.onerror = null;
        this.classWs.onclose = null;
        if (this.classWs.readyState === WebSocket.OPEN || this.classWs.readyState === WebSocket.CONNECTING) {
          this.classWs.close(1000, '页面结束');
        }
        this.classWs = null;
      }
      this.classWsStatus = 'idle';
    },

    /**
     * 课堂消息分发
     * 文本帧 = 控制指令：scriptAudio 推一页讲稿 / scriptAudioDone 本页音频已下发完 / classEnd 下课
     * 二进制帧 = PCM 音频分片，收到即入队播放
     */
    handleClassWsMessage(event) {
      if (event.data instanceof ArrayBuffer) {
        // 裸 PCM 分片：入队并按时间轴排程，第一片到达就能出声，不用等整页收完
        this.enqueuePcmData(event.data);
        return;
      }
      let msg = null;
      try {
        msg = JSON.parse(event.data);
      } catch (e) {
        console.warn('课堂消息不是合法JSON，已忽略:', event.data);
        return;
      }
      if (!msg || !msg.type) {
        return;
      }
      if (msg.type === MSG_SCRIPT_AUDIO) {
        this.handleScriptAudioMessage(msg);
      } else if (msg.type === MSG_SCRIPT_AUDIO_DONE) {
        // 本页 PCM 已全部下发，但不代表播完了，交给播完检测去判断
        this.audioDone = true;
        this.checkPageAudioFinished();
      } else if (msg.type === MSG_CLASS_END) {
        this.handleClassEnd();
      } else if (msg.type === MSG_HEARTBEAT) {
        // 心跳保活：讲稿生成期间后端每隔 20 秒发一次，防止 nginx 把静默当成失联掐断连接。
        // 前端不需要做任何事，收下即等于「连接还活着」，故意不刷日志免得刷屏。
      }
    },

    /**
     * 处理「推一页讲稿」：换页 + 展示讲稿，并清掉上一页残留的音频
     * <p>
     * 本页语音不在这里触发：后端紧跟其后会连发若干二进制帧（PCM），
     * 由 handleClassWsMessage 的二进制分支入队播放，最后以 scriptAudioDone 收尾。
     */
    handleScriptAudioMessage(msg) {
      const content = msg.content || {};
      // 分母（aiTotalPage）不在这条通道里取：它用的是 enterLive 下发的完整课件页数，
      // 见 initClassroom 里的 roomInfo.courseWarePageSize，全程只填一次、不会被覆盖。
      // 后端已不再随每页下发 totalPage —— 那个值原本是「可讲页数」（过滤掉没有页块的
      // 幻灯片之后的数量），与本条 pageNumber 的「真实幻灯片页码」不是一个口径，
      // 拿它当分母会算出「第 7 / 5 页」这种荒谬结果。
      // 页码以后端下发为准（真实幻灯片页码，从 1 开始），它驱动 PPT 翻页。
      // 判定用 typeof 而不是直接 if(msg.pageNumber)：0 在 JS 里是 falsy，
      // 若后端某次下发 0（例如课后总结不计页），用 if 会把它当成「没下发」而漏翻/错翻。
      if (typeof msg.pageNumber === 'number' && msg.pageNumber > 0) {
        this.aiPageNumber = msg.pageNumber;
      }
      // content 里只有 script（讲稿文本），讲稿之外的内容一律不展示
      this.aiScript = content.script || '';
      // 字幕时间轴：后端随本页讲稿一起下发，毫秒、相对本页音频起点。
      // 虽名为 words，实际粒度是「句」（后端已把单字按标点聚合），
      // 每项为 { text, beginTime, endTime }，前端按句切换显示即可。
      this.aiSentences = Array.isArray(content.words) ? content.words : [];
      this.classEnded = false;
      // 换页先清场：停掉上一页还在排程的音频、丢弃未播完的队列、重置收齐标记
      this.stopPcmPlayback();
    },

    /**
     * 音频解锁：AudioContext 被浏览器自动播放策略挂起时，靠这次点击把它唤醒
     */
    unlockAiAudio() {
      this.aiAudioBlocked = false;
      if (this.playbackAudioContext) {
        this.playbackAudioContext.resume().catch(() => {});
      }
    },

    /**
     * 收到下课通知（后端讲完了整节课）
     * <p>
     * 这里必须走完整的释放流程，不能只置个标记：releaseAiClass 会断开 WebSocket
     * 并清掉 classSessionId，而「开始上课」的可点条件（classCanStart）正是看这两个状态。
     * 只设 classEnded 的话，会话ID一直留着，按钮就永远点不亮了。
     * 释放之后可以再点「开始上课」，后端会重新开一节新课（若旧会话还没注销，后端会复用原会话）。
     */
    handleClassEnd() {
      this.classEnded = true;
      this.releaseAiClass();
      this.$message.success('本节课已结束');
    },

    /**
     * 前端上报消息（唯一出口）
     * 后端只认三种：scriptAudioFinished（本页播完，可翻页）、stop（下课）、restart（从头开始）
     */
    sendClassMessage(payload) {
      if (this.classWs && this.classWs.readyState === WebSocket.OPEN) {
        this.classWs.send(JSON.stringify(payload));
        return true;
      }
      console.warn('课堂 WebSocket 未连接，消息未发送:', payload);
      return false;
    },

    /**
     * 结束本节课：先告知后端（后台推进线程会收口），再释放本地资源
     */
    stopAiClass() {
      this.sendClassMessage({ type: CMD_STOP });
      this.releaseAiClass();
      // 主动下课，面板不显示「已下课」，因为马上就可以重新开课
      this.classEnded = false;
      this.$message.info('已结束本节课');
    },

    /**
     * 从头开始学习
     * <p>
     * 只发一条 restart 指令，其余交给后端推进线程处理：它会把页码复位到第 1 页、
     * 作废已下发的音频并重新生成第一页，随后照常下发 scriptAudio。
     * 前端要做的是「立刻闭嘴并清场」——不停播的话，复位前后两页的音频会叠在一起。
     * 不断开 WebSocket、也不重置 sessionId：课堂还是同一节，只是进度回到起点。
     */
    restartAiClass() {
      if (!this.sendClassMessage({ type: CMD_RESTART })) {
        return;
      }
      // 本地清场：停播 + 丢掉还在队列里的旧音频，等后端推第 1 页
      this.stopPcmPlayback();
      this.aiScript = '';
      this.aiSentences = [];
      this.aiPageNumber = 1;
      this.classEnded = false;
      this.$message.success('已从头开始，正在重新讲第 1 页');
    },

    /**
     * 释放 AI 上课占用的全部本地资源（下课、手动结束、页面销毁共用）
     * <p>
     * 不动 classEnded：它是给面板看的展示状态，由调用方按场景自己维护——
     * 后端下课要显示「已下课」，手动结束/重开则不显示，混在一起改会互相打架。
     */
    releaseAiClass() {
      // 先停播再释放其它资源：声音必须立刻停，否则下课了还在念
      this.stopPcmPlayback();
      this.disconnectClassWs();
      this.classSessionId = '';
      this.aiScript = '';
      this.aiSentences = [];
      this.aiPageNumber = 1;
      this.aiAudioBlocked = false;
    },

    // ==================== AI 上课 · PCM 播放（后端下发裸PCM二进制帧） ====================
    /**
     * 收到一段 PCM 就入队，并按时间轴无缝衔接播放
     * 不整段等收完再播，而是每收到一片就排到上一片后面，首片到达即出声
     */
    enqueuePcmData(arrayBuffer) {
      this.pcmBufferQueue.push(arrayBuffer);
      this.schedulePcmPlayback();
    },

    /**
     * 惰性创建播放用的 AudioContext（首次收到音频才创建，避免无音频时白占音频设备）
     * <p>
     * 这里刻意不给 AudioContext 指定采样率：指定了只有部分浏览器认（Safari 会忽略），
     * 一旦被忽略，后面读到的 sampleRate 就和真实数据对不上。真正要紧的是建 AudioBuffer
     * 时用的采样率（见 schedulePcmPlayback），那个必须固定为 PCM_SAMPLE_RATE。
     */
    ensurePlaybackContext() {
      if (!this.playbackAudioContext) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        this.playbackAudioContext = new Ctx();
        this.playbackGainNode = this.playbackAudioContext.createGain();
        this.playbackGainNode.gain.value = 1.0;
        this.playbackGainNode.connect(this.playbackAudioContext.destination);
        this.nextPlayTime = this.playbackAudioContext.currentTime;
      }
      return this.playbackAudioContext;
    },

    /**
     * 调度播放队列：每段按 nextPlayTime 依次排程，保证首尾相接不卡顿
     * <p>
     * 自动播放策略会让 AudioContext 停在 suspended，此时排进去的音频不会被解码播出，
     * 所以发现挂起就亮出「点击开启声音」，等学生点一下再唤醒。
     */
    schedulePcmPlayback() {
      if (this.pcmBufferQueue.length === 0) {
        return;
      }
      const ctx = this.ensurePlaybackContext();
      if (ctx.state === 'suspended') {
        ctx.resume();
        this.aiAudioBlocked = true;
      }
      // 本页音频的第一片落轨时，记下它在 AudioContext 时间轴上的起点：
      // 字幕进度 = (ctx.currentTime - pageAudioStartTime) * 1000，与音频天然同源，不会漂移
      if (!this.pageAudioStarted) {
        this.pageAudioStarted = true;
        this.pageAudioStartTime = Math.max(this.nextPlayTime, ctx.currentTime);
        this.startSubtitleTicker();
      }
      while (this.pcmBufferQueue.length > 0) {
        const pcmBuffer = this.pcmBufferQueue.shift();
        const floatSamples = this.int16ToFloat32(pcmBuffer);
        if (!floatSamples || floatSamples.length === 0) {
          continue;
        }
        // 按16000建AudioBuffer：数据本身是16k的，用设备采样率去装会把语速拉快
        const audioBuffer = ctx.createBuffer(PCM_CHANNELS, floatSamples.length, PCM_SAMPLE_RATE);
        audioBuffer.getChannelData(0).set(floatSamples);
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(this.playbackGainNode);
        // 排到上一段结束的位置；若已落后于当前时间（网络抖动导致断档），就从现在开始，不追帧
        const startTime = Math.max(this.nextPlayTime, ctx.currentTime);
        source.start(startTime);
        this.nextPlayTime = startTime + audioBuffer.duration;
      }
      // 队列排空的这一刻起，按「还要放多久」约个闹钟回来看本页有没有播完
      this.schedulePageFinishCheck();
    },

    /**
     * 16bit PCM(小端) 转 Float32，交给 Web Audio 播放
     * 16bit 有符号取值 -32768~32767，除以 32768 归一化到 -1~1
     */
    int16ToFloat32(arrayBuffer) {
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        return null;
      }
      const view = new DataView(arrayBuffer);
      const length = Math.floor(arrayBuffer.byteLength / 2);
      const floatSamples = new Float32Array(length);
      for (let i = 0; i < length; i++) {
        const int16 = view.getInt16(i * 2, true);
        floatSamples[i] = Math.max(-1, Math.min(1, int16 / 32768));
      }
      return floatSamples;
    },

    /**
     * 本页音频播完了吗？播完就上报 scriptAudioFinished，后端课堂推进线程收到才会下发下一页
     * 三个条件都满足才算完：数据收齐(scriptAudioDone到了) + 队列已空(全排进音轨) + 排到的结束时间已过(真播完了)
     */
    checkPageAudioFinished() {
      if (!this.audioDone || this.classEnded) {
        return;
      }
      if (this.pcmBufferQueue.length > 0) {
        // 还有没排进音轨的分片，说明音频仍在陆续到达
        return;
      }
      const ctx = this.playbackAudioContext;
      if (ctx && this.nextPlayTime > ctx.currentTime) {
        // 音轨里还排着没播完的内容
        return;
      }
      this.audioDone = false;
      this.sendClassMessage({ type: CMD_SCRIPT_AUDIO_FINISHED });
    },

    /**
     * 约一个「最后一段播完」的闹钟，到时回来判断本页是否结束
     * 多留 150ms 余量，避免卡在调度尾巴上误判
     */
    schedulePageFinishCheck() {
      if (this.pageFinishTimer) {
        clearTimeout(this.pageFinishTimer);
        this.pageFinishTimer = null;
      }
      const ctx = this.playbackAudioContext;
      if (!ctx) {
        return;
      }
      // 到这里队列已排空，nextPlayTime 就是本页音频的绝对终点；
      // 减去本页起点即得音频总时长，字幕显示到这一刻就收尾
      this.subtitleStopMs = Math.max(0, Math.round((this.nextPlayTime - this.pageAudioStartTime) * 1000));
      const restMs = Math.max(0, (this.nextPlayTime - ctx.currentTime) * 1000) + 150;
      this.pageFinishTimer = setTimeout(() => {
        this.pageFinishTimer = null;
        this.checkPageAudioFinished();
      }, restMs);
    },

    /**
     * 启动播放进度采样：每 100ms 把「已播毫秒」写进 subtitleElapsedMs
     * <p>
     * 字幕渲染完全由这个进度值驱动（computed 里按 beginTime 匹配片段），
     * 所以采样精度就是字幕的跟读精度。100ms 对人眼已足够顺滑，
     * 且比 requestAnimationFrame(约16ms)省得多——字幕是文本，不需要 60fps。
     */
    startSubtitleTicker() {
      this.stopSubtitleTicker();
      this.subtitleElapsedMs = 0;
      this.subtitleTimer = setInterval(() => {
        const ctx = this.playbackAudioContext;
        if (!ctx || !this.pageAudioStarted) {
          return;
        }
        this.subtitleElapsedMs = Math.max(0, Math.round((ctx.currentTime - this.pageAudioStartTime) * 1000));
      }, 100);
    },

    /** 停止播放进度采样（换页、下课、销毁都要停，否则定时器会一直空转） */
    stopSubtitleTicker() {
      if (this.subtitleTimer) {
        clearInterval(this.subtitleTimer);
        this.subtitleTimer = null;
      }
    },

    /**
     * 停止播放并清空队列：换页、下课、页面销毁都走这里
     * <p>
     * 直接关掉 AudioContext 是最彻底的停法——已排程的 BufferSource 会随上下文一起失效，
     * 不用逐个 source.stop()；代价是下次播放要重建上下文，开销很小。
     */
    stopPcmPlayback() {
      if (this.pageFinishTimer) {
        clearTimeout(this.pageFinishTimer);
        this.pageFinishTimer = null;
      }
      if (this.playbackAudioContext && this.playbackAudioContext.state !== 'closed') {
        this.playbackAudioContext.close().catch(() => {});
      }
      this.playbackAudioContext = null;
      this.playbackGainNode = null;
      this.pcmBufferQueue = [];
      this.nextPlayTime = 0;
      // 字幕跟读一并复位：进度采样停掉、本页起点清空，等下一页音频到达重新起算
      this.stopSubtitleTicker();
      this.pageAudioStarted = false;
      this.pageAudioStartTime = 0;
      this.subtitleStopMs = 0;
      this.subtitleElapsedMs = 0;
      this.audioDone = false;
    },

    // ==================== 工具方法 ====================
    getVideoDimension() {
      const dimensionMap = {
        '360p': 'VD_640x360',
        '480p': 'VD_840x480',
        '720p': 'VD_1280x720',
        '1080p': 'VD_1920x1080'
      }
      return dimensionMap[this.videoQuality] || 'VD_1280x720'
    }
  }
}
</script>

<style scoped>
/* ==================== 整体布局 ==================== */
.rtc-live-classroom {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #0a0e27;
  color: #e0e0e0;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  overflow: hidden;
}

/* ==================== 顶部导航栏 ==================== */
.classroom-header {
  height: 56px;
  background: linear-gradient(135deg, #1a1f3a 0%, #1e2a4a 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-bottom: 1px solid rgba(100, 140, 255, 0.15);
  flex-shrink: 0;
  z-index: 10;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.course-info {
  display: flex;
  flex-direction: column;
}

.course-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: 0.5px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* ==================== 主体内容区 ==================== */
.classroom-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

/* ==================== 视频区域 ==================== */
.video-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #0d1127;
  position: relative;
  min-width: 0;
}

/* 老师屏幕共享大屏 */
.teacher-video-container {
  flex: 1;
  position: relative;
  background: #0a0d1a;
  margin: 12px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(100, 140, 255, 0.1);
  min-height: 300px;
}

.screen-share-video {
  width: 100%;
  height: 100%;
}

.screen-share-video video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.screen-share-label {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ==================== AI 课件展示区（aiFlag=1 时替代等待共享屏幕占位） ==================== */
.ppt-courseware {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  z-index: 2;
}

.ppt-courseware-label {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 3;
  white-space: nowrap;
}

/* ==================== AI 上课：音频解锁入口 ==================== */
/* 音频被浏览器自动播放策略拦截时的解锁按钮 */
.ai-audio-unlock {
  position: absolute;
  right: 16px;
  top: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #4c8bf5;
  color: #fff;
  border-radius: 20px;
  font-size: 13px;
  cursor: pointer;
  z-index: 4;
  box-shadow: 0 4px 14px rgba(76, 139, 245, 0.45);
}

.ai-audio-unlock:hover {
  background: #3d7ae0;
}

/* ==================== AI 课堂状态（头部展示） ==================== */
/* 原右侧面板的「当前进度」迁移到头部，与 RTC/课堂状态标签并排 */
.header-class-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #b0b8d0;
  white-space: nowrap;
}

.header-class-divider {
  width: 1px;
  height: 18px;
  background: rgba(100, 140, 255, 0.25);
}

/* 已下课 状态标记 */
.ai-qa-flag {
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  background: rgba(76, 175, 132, 0.18);
  color: #4caf84;
}

/* ==================== AI 上课字幕（底部控制栏正中，按句跟随语音切换） ==================== */
/* 字幕固定在整个底部控制栏的水平中心，一次只显示当前正在朗读的那一句。
   为什么用绝对定位而不是 flex:1 占位：控制栏左右两侧宽度不等
   （左边两个摄像头预览窗、右边一整组按钮），flex:1 的字幕项一旦被 max-width
   卡住，剩下的空隙会被 .control-right 的 margin-left:auto 全部吸走，
   字幕就被推到摄像头右侧、并不居中。改以 .control-bar 为基准绝对居中，
   位置就与两侧宽窄无关了；pointer-events:none 保证不拦截摄像头与按钮的点击。 */
.ai-subtitle {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  max-width: 42%;
  padding: 6px 18px;
  border-radius: 10px;
  background: rgba(8, 12, 28, 0.55);
  border: 1px solid rgba(100, 140, 255, 0.18);
  font-size: 15px;
  line-height: 1.5;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  color: #ffffff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  pointer-events: none;
}

/* ==================== 底部控制栏：AI 操作按钮与图标之间的分隔 ==================== */
.control-right-divider {
  width: 1px;
  height: 24px;
  background: rgba(100, 140, 255, 0.25);
  margin: 0 4px;
}

/* ==================== 底部摄像头条（老师/学生摄像头悬浮窗迁移至控制栏） ==================== */
.camera-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 预览窗：我的摄像头 96x54，老师摄像头与原开关按钮同尺寸 40x40 圆窗 */
.camera-bar-video-wrap {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #0a0d1a;
  border: 1px solid rgba(100, 140, 255, 0.25);
}

.self-camera-bar .camera-bar-video-wrap {
  width: 96px;
  height: 54px;
}

.teacher-camera-bar .camera-bar-video-wrap {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.camera-bar-video {
  width: 100%;
  height: 100%;
}

.camera-bar-video video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 学生本地预览保持镜像翻转 */
.self-camera-bar .camera-bar-video video {
  transform: scaleX(-1);
}

.camera-bar-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: #4a5588;
  font-size: 11px;
  background: #0a0d1a;
}

.camera-bar-placeholder i {
  font-size: 16px;
}

.teacher-camera-bar .camera-bar-placeholder i {
  font-size: 20px;
}

/* 麦克风/摄像头开关小图标（叠加在我的预览窗内部右下角） */
.camera-bar-actions {
  position: absolute;
  right: 4px;
  bottom: 4px;
  display: flex;
  gap: 4px;
}

.camera-action-btn {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  font-size: 12px;
  transition: background 0.3s;
}

.camera-action-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.camera-action-btn.muted {
  background: rgba(255, 80, 80, 0.75);
}

/* 老师名字（小圆窗右侧） */
.teacher-bar-name {
  font-size: 12px;
  color: #b0b8d0;
}

.video-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #4a5588;
}

.video-placeholder i {
  font-size: 64px;
  display: block;
  margin-bottom: 12px;
}

.video-placeholder p {
  font-size: 14px;
  margin: 0;
}

/* 学生视频列表 */
.student-video-list {
  display: flex;
  gap: 10px;
  padding: 0 12px 12px;
  overflow-x: auto;
  flex-shrink: 0;
  min-height: 140px;
}

.student-video-list::-webkit-scrollbar {
  height: 4px;
}

.student-video-list::-webkit-scrollbar-thumb {
  background: rgba(100, 140, 255, 0.3);
  border-radius: 2px;
}

.student-video-item {
  width: 180px;
  height: 130px;
  flex-shrink: 0;
  position: relative;
  background: #0a0d1a;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: border-color 0.3s;
}

.student-video-item.speaking {
  border-color: #4caf84;
  box-shadow: 0 0 12px rgba(76, 175, 132, 0.3);
}

.student-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.student-label {
  position: absolute;
  bottom: 6px;
  left: 6px;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(6px);
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  color: #ccc;
  display: flex;
  align-items: center;
  gap: 4px;
}

.video-placeholder-mini {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #3a4478;
}

.video-placeholder-mini i {
  font-size: 36px;
}

/* ==================== 底部导航栏（与顶部导航栏风格一致） ==================== */
.classroom-footer {
  background: linear-gradient(135deg, #1a1f3a 0%, #1e2a4a 100%);
  display: flex;
  align-items: center;
  padding: 4px 20px;
  border-top: 1px solid rgba(100, 140, 255, 0.15);
  flex-shrink: 0;
  z-index: 10;
}

/* ==================== 底部控制栏 ==================== */
.control-bar {
  /* 作为 AI 字幕绝对定位的基准容器 */
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
}

/* 老师摄像头紧跟在我的摄像头后面，靠左排列 */
.teacher-camera-bar {
  margin-left: 16px;
}

.control-right {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.control-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 18px;
  color: #b0b8d0;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.control-btn.muted {
  background: rgba(255, 80, 80, 0.2);
  color: #ff5050;
}


/* ==================== 设置弹窗 ==================== */
.settings-content {
  padding: 10px 0;
}

.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid rgba(100, 140, 255, 0.08);
}

.setting-label {
  font-size: 14px;
  color: #d0d8f0;
  min-width: 70px;
}

.setting-item >>> .el-select {
  width: 240px;
}

/* ==================== 弹窗样式覆盖 ==================== */
>>> .el-dialog {
  background: #1a1f3a;
  border-radius: 12px;
}

>>> .el-dialog__title {
  color: #d0d8f0;
}

>>> .el-dialog__body {
  color: #b0b8d0;
}

>>> .el-message-box {
  background: #1a1f3a;
  border: 1px solid rgba(100, 140, 255, 0.2);
}

>>> .el-message-box__title {
  color: #d0d8f0;
}

>>> .el-message-box__message {
  color: #b0b8d0;
}

/* ==================== 响应式 ==================== */
@media (max-width: 1024px) {
  .student-video-item {
    width: 140px;
    height: 105px;
  }
  /* 屏幕变窄：字幕进一步收窄，避免压到两侧的摄像头与按钮 */
  .ai-subtitle {
    max-width: 34%;
    padding: 5px 12px;
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .student-video-item {
    width: 120px;
    height: 90px;
  }
  /* 手机端空间有限：状态标签的字号收紧，字幕进一步缩小 */
  .header-class-progress {
    font-size: 12px;
  }
  .ai-subtitle {
    max-width: 26%;
    font-size: 12px;
    padding: 4px 10px;
  }
}
</style>
